import React, { useMemo } from 'react'
import { dataIdFromObject } from '@sketch/graphql-cache'
import {
  handleFetchMore,
  useModalContext,
  LinkButton,
  Section,
} from '@sketch/components'
import {
  useGetUserSettingsQuery,
  UserWorkspaceMembershipFragment,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import {
  routes,
  useUserAuthorizations,
  getIsForInAppPresentation,
  useUserProfile,
  isSsoAuthorization,
  getPersonalAuthorization,
} from '@sketch/modules-common'
import LeaveWorkspaceModal from '../../../modals/LeaveWorkspaceModal'
import WorkspaceList from './WorkspaceList'
import { LinkNonSSO } from './LinkNonSSO'

const WorkspacesSettings: React.FC<{
  currentWorkspace?: WorkspaceMinimalFragment
}> = ({ currentWorkspace }) => {
  const { data: userData } = useUserProfile()
  const { showModal } = useModalContext()
  const { data: userSettingsData, fetchMore } = useGetUserSettingsQuery()
  const isForInAppPresentation = getIsForInAppPresentation()
  const { authorizations, hasPersonalAuthorization } = useUserAuthorizations()

  const user = userData?.me
  const memberships: UserWorkspaceMembershipFragment[] =
    userSettingsData?.me?.workspaceMemberships?.entries || []

  const handleLoadMore = handleFetchMore(
    fetchMore,
    ['me', 'workspaceMemberships', 'entries'],
    {
      after: userSettingsData?.me?.workspaceMemberships?.meta.after,
      dataIdFromObject,
    }
  )

  const hasMASubscription = memberships.some(
    membership =>
      membership?.workspace?.customer?.source === 'macappstore' &&
      membership?.workspace?.userIsOwner
  )

  const isSsoOnly = !getPersonalAuthorization()

  const ssoWorkspaces = useMemo(() => {
    return userSettingsData?.me?.workspaceMemberships?.entries.filter(
      membership => membership.workspace.customer?.ssoEnabled
    )
  }, [userSettingsData])

  if (!user) {
    return null
  }

  const showLinkNonSSO =
    ssoWorkspaces?.length &&
    (isSsoOnly || (!isSsoOnly && user.hasPersonalIdentity))

  const isSignedInWithSso = authorizations.some(isSsoAuthorization)

  if (memberships.length === 0) {
    return null
  }

  return (
    <Section
      title="Workspaces"
      action={
        showLinkNonSSO && currentWorkspace ? (
          <LinkNonSSO
            linkedEmail={user.personalIdentityEmail}
            currentWorkspace={currentWorkspace}
            ssoWorkspaces={ssoWorkspaces}
            isSignedInWithSSO={isSignedInWithSso}
          />
        ) : null
      }
      align="center"
    >
      <WorkspaceList
        memberships={memberships}
        onLoadMore={handleLoadMore}
        onLeaveWorkspace={membership => {
          showModal(LeaveWorkspaceModal, {
            membershipId: membership.identifier,
            workspace: membership.workspace,
            userHasPersonalIdentity: user.hasPersonalIdentity,
          })
        }}
        linkButton={
          hasPersonalAuthorization &&
          !hasMASubscription &&
          !isForInAppPresentation && (
            <LinkButton
              to={routes.WORKSPACE_CREATE.create({})}
              variant="primary"
            >
              Create Workspace&hellip;
            </LinkButton>
          )
        }
      />
    </Section>
  )
}

export default WorkspacesSettings
